'use client';

import { useMemoizedFn, useRequest } from 'ahooks';
import { message } from 'antd';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import LoadMore from '@/components/LoadMore';
import LottieJumpLoading from '@/components/chat-app/components/LottieJumpLoading';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import { getSessionId } from '@/service/create';
import { getTaskList } from '@/service/task';
import useNoteItemStore from '@/store/useNoteItemStore';
import { type TaskData, TaskState } from '@/types/task';
import { EVENT } from '@/utils/event';

import TaskItem from '../TaskItem';
import styles from './index.less';

interface IProps {
  mindId: string;
  sessionId: string;
}

interface IPagination {
  pageNo: number;
  pageSize: number;
}

const TaskFlow = (props: IProps) => {
  const { mindId, sessionId } = props;

  const [taskList, setTaskList] = useState<TaskData[]>([]);
  const [total, setTotal] = useState<number | undefined>(undefined);

  const [layoutSize] = useStorage('layoutSize');

  const initPagination = { pageNo: 1, pageSize: 10 };
  const paginationRef = useRef<IPagination>(initPagination);
  const [stopLoadMore, setStopLoadMore] = useState<boolean>(false);

  const { t } = useTranslation();

  // 每次打开task会重新获取taskList，重新获取sessionId，
  useEffect(() => {
    const fetchSessionId = async () => {
      const id = await getSessionId();

      if (id) {
        dispatchEvent(new CustomEvent(EVENT.SET_CURRENT_SESSION_ID, { detail: id }));
      }
    };

    fetchSessionId();
  }, [taskList]);

  const { run: searchTask, loading } = useRequest(getTaskList, {
    manual: true,
    onSuccess(res) {
      if (res.data.code === 0) {
        setTaskList(res.data.data.list);
        setTotal(res.data.data.total);

        if (res.data.data.list.length < paginationRef.current.pageSize) {
          setStopLoadMore(true);
        }
      } else {
        message.error(res.data.message || 'Failed to retrieve the list of chats.');
      }
    },
    onError: (error) => {
      message.error(error.message);
      setTaskList([]);
    }
  });

  const loadPageList = useCallback((): Promise<void> => {
    paginationRef.current = {
      ...paginationRef.current,
      pageNo: paginationRef.current.pageNo + 1
    };

    return getTaskList({ mindId, ...paginationRef.current }).then((res) => {
      setTaskList([...taskList, ...res.data.data.list]);
      setTotal(res.data.data.total);

      if (res.data.data.list.length < paginationRef.current.pageSize) {
        setStopLoadMore(true);
      }
    });
  }, [mindId, taskList]);

  const [todayTaskList, previousTaskList] = useMemo(() => {
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000; // 24小时之前的时间
    const recent: TaskData[] = [];
    const older: TaskData[] = [];

    const _taskList = taskList.filter((task) => task.state !== TaskState.NOT_EXECUTED);

    _taskList.forEach((task) => {
      const taskTime = new Date(task.gmtModified).getTime(); // 获取任务的修改时间

      if (taskTime > oneDayAgo) {
        // 如果任务修改时间在24小时之内
        recent.push(task);
      } else {
        // 否则，认为任务是24小时之前的
        older.push(task);
      }
    });

    return [recent, older];
  }, [taskList]);

  const handleClickDelete = useMemoizedFn((taskId: number) => {
    let sessionIdToDelete: string | undefined = undefined;

    const newList = taskList.filter((task) => {
      if (task.id === taskId) {
        sessionIdToDelete = task.sessionId;

        return false;
      }

      return true;
    });

    if (sessionIdToDelete) {
      useNoteItemStore.getState().addDeleteItemIdByChatSession(sessionIdToDelete);
    }

    setTaskList([...newList]);
  });

  const renderTaskItem = useCallback(
    (renderList: TaskData[]) => {
      return renderList.map((task) => (
        <TaskItem
          key={task.id}
          className={styles.taskItem}
          current={sessionId === task.sessionId}
          mindId={mindId}
          onDelete={handleClickDelete}
          task={task}
        />
      ));
    },
    [handleClickDelete, mindId, sessionId]
  );

  const handleSearch = useCallback(() => {
    if (mindId) {
      searchTask({ mindId, ...paginationRef.current });
    }
  }, [mindId, searchTask]);

  useEffect(() => {
    handleSearch();
    addEventListener(EVENT.UPDATE_TASK_LIST, handleSearch);

    return () => {
      removeEventListener(EVENT.UPDATE_TASK_LIST, handleSearch);
    };
  }, [handleSearch]);

  useEffect(() => {
    const handleDelete = (e: Event) => {
      const event = e as CustomEvent;
      const newList = taskList.filter((task) => task.sessionId !== event.detail);

      setTaskList(newList);
    };

    addEventListener(EVENT.DELETE_SESSION_CB, handleDelete);

    return () => {
      removeEventListener(EVENT.DELETE_SESSION_CB, handleDelete);
    };
  }, [taskList]);

  if (loading) {
    return <LottieJumpLoading className={styles.loading} />;
  }

  if (!taskList.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-[240px]">
        <img alt="default logo" className="w-[56px] h-[32px]" src="./default_logo.svg" />
        <div className="mt-1 text-[#643e54] text-[20px] font-medium leading-[32px]">
          {t('chatApp.header.historyEmpty')}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.container)}>
      {/* Today */}
      {!!todayTaskList.length && (
        <>
          <div className="mb-4 text-[#ACABB3] text-[16px] font-medium leading-[24px]">
            {t('chatApp.header.today')}
          </div>
          {renderTaskItem(todayTaskList)}
        </>
      )}
      {/* Previous */}
      {!!previousTaskList.length && (
        <>
          <div className="mb-4 text-[#ACABB3] text-[16px] font-medium leading-[24px]">
            {t('chatApp.header.previous')}
          </div>
          {renderTaskItem(previousTaskList)}
        </>
      )}
      {!!total && taskList.length < total && !stopLoadMore && (
        <LoadMore
          className={classNames(styles.loadMore, {
            [styles.inMobile]: layoutSize === LAYOUT_SIZE.SMALL
          })}
          loadMore={loadPageList}
          scrollContainerId="#TaskFlowContainer"
        />
      )}
    </div>
  );
};

export default TaskFlow;
